import { dateFormatters, formatCurrencyNatural, makeCent } from '@frontend/duck-tape';
import { Button, CardContainer, Divider, Text, XStack, YStack } from '@frontend/web-react';
import { CheckMark } from '@frontend/web/components/DomainSpecific/Onboarding/Complete/CheckMark';
import { useAnalytics } from '@frontend/web/hooks';
import { PromotionBanner } from '../Membership/PromotionBanner';

export type OnboardingCompleteState = {
  onboardingSessionId?: string;
  paidToday?: Cent;
  planCode?: string;
  promotionId?: string;
  promotionName?: string;
  purchaseType?: 'Subscription';
  renewalDate?: DateISOString;
  renewalPlanLabel?: string;
  step?: 'complete' | 'paymentCapture' | 'selectPlan';
};

export type OnboardingCompleteScreenProps = {
  onComplete: () => void;
  state?: OnboardingCompleteState;
};

const PaymentSuccessLineItem = ({ label, value }: { label: string; value: string }) => {
  return (
    <XStack alignItems="center" justifyContent="between" py="sm">
      <Text type="p-sm">{label}</Text>
      <Text color="primary" type="p-sm">
        {value}
      </Text>
    </XStack>
  );
};

export const OnboardingCompleteScreen = ({ onComplete, state }: OnboardingCompleteScreenProps) => {
  const { track } = useAnalytics();
  const hasReceiptData = state?.paidToday !== undefined;

  const handleComplete = () => {
    track({
      data: {
        onboardingSessionId: state?.onboardingSessionId,
        planCode: state?.planCode,
        platform: 'web',
        purchaseType: state?.purchaseType,
        step: state?.step,
      },
      event: 'membershipCompleteCTASelected',
      topic: 'onboarding',
    });

    onComplete();
  };

  return (
    <YStack gapY="lg">
      <YStack alignItems="center" gapY="md">
        <CheckMark />
        <Text textAlign="center" type="h3">
          {hasReceiptData ? 'Payment successfully confirmed!' : 'Welcome to Duckbill!'}
        </Text>
        {!hasReceiptData ? (
          <Text className="max-w-md" color="secondary" textAlign="center" type="p-md">
            {"You're"} all set up and ready to go. Start creating tasks and let us help you get things done.
          </Text>
        ) : null}
      </YStack>

      {hasReceiptData ? (
        <CardContainer>
          {state?.promotionName ? (
            <YStack mb="md">
              <PromotionBanner promotionName={state.promotionName} />
            </YStack>
          ) : null}

          <YStack>
            {state?.paidToday !== undefined ? (
              <>
                <PaymentSuccessLineItem label="Paid today" value={formatCurrencyNatural(state.paidToday)} />
                <Divider />
              </>
            ) : null}
            {state?.renewalDate ? (
              <>
                <PaymentSuccessLineItem label="Renewal date" value={dateFormatters['LL'](state.renewalDate)} />
                <Divider />
              </>
            ) : null}
            {state?.renewalPlanLabel ? (
              <PaymentSuccessLineItem
                label="Renewal plan"
                value={`${state.renewalPlanLabel} ${formatCurrencyNatural(state.paidToday ?? makeCent(0))}/mo`}
              />
            ) : null}
          </YStack>
        </CardContainer>
      ) : null}

      <Button label="Continue" onClick={handleComplete} variant="primary" />
    </YStack>
  );
};
